<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useStore,
  useStoreGetters,
} from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TelegramCampaignPreview from 'dashboard/components-next/Campaigns/Pages/CampaignPage/TelegramCampaign/TelegramCampaignPreview.vue';
import telegramAudienceAPI from 'dashboard/routes/dashboard/settings/telegram/api/telegramAudienceAPI';

const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();
const campaignUiFlags = useMapGetter('campaigns/getUIFlags');

const campaignId = computed(() => Number(route.params.id || 0));

const allCampaigns = computed(
  () => getters['campaigns/getAllCampaigns'].value || []
);

const campaign = computed(() =>
  allCampaigns.value.find(item => Number(item.id) === campaignId.value)
);

const telegramData = computed(
  () => campaign.value?.template_params?.telegram || {}
);

const analytics = computed(() => telegramData.value?.analytics || {});

const blocksIncludePrimary = computed(() =>
  Boolean(
    telegramData.value?.blocks_include_primary ||
      telegramData.value?.blocksIncludePrimary
  )
);

const sendCount = computed(
  () =>
    Number(analytics.value?.sent_count ?? analytics.value?.target_count ?? 0) ||
    0
);

const clickCount = computed(
  () =>
    Number(
      analytics.value?.total_clicks ?? analytics.value?.click_count ?? 0
    ) || 0
);

const ctrValue = computed(() => {
  if (!sendCount.value) return '0.00%';
  return `${((clickCount.value / sendCount.value) * 100).toFixed(2)}%`;
});

const buttonClickCounts = computed(() => analytics.value?.button_clicks || {});

const isLoading = computed(
  () => campaignUiFlags.value.isFetching && !campaign.value
);

const formatDateTime = epochSeconds => {
  const seconds = Number(epochSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';

  const date = new Date(seconds * 1000);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const normalizeType = type => {
  const normalized = (type || '')
    .toString()
    .trim()
    .toLowerCase()
    .replaceAll(' ', '_');
  if (normalized === 'photo') return 'image';
  return normalized;
};

const normalizeButtons = (buttons, contextKey) =>
  (Array.isArray(buttons) ? buttons : [])
    .map((button, index) => ({
      text: button?.text || '',
      url: button?.url || button?.web_app?.url || '',
      tracking_key:
        button?.tracking_key ||
        button?.trackingKey ||
        `${contextKey}:button:${index}`,
    }))
    .filter(button => button.text && button.url);

const previewButtons = computed(() =>
  blocksIncludePrimary.value
    ? []
    : normalizeButtons(telegramData.value?.buttons, 'primary')
);

const previewBlocks = computed(() =>
  (Array.isArray(telegramData.value?.blocks)
    ? telegramData.value.blocks
    : []
  ).map((block, blockIndex) => {
    const type = normalizeType(block?.type);
    const baseBlock = {
      id: block?.id || `report-block-${blockIndex}`,
      type,
      text: block?.text || '',
      caption: block?.caption || '',
      media_url: block?.media_url || block?.image_url || '',
      buttons: normalizeButtons(block?.buttons, `block:${blockIndex}`),
    };

    if (type === 'media_group') {
      baseBlock.items = Array.isArray(block?.items) ? block.items : [];
      baseBlock.follow_up_text = block?.follow_up_text || '';
    }

    if (type === 'carousel') {
      baseBlock.cards = (Array.isArray(block?.cards) ? block.cards : []).map(
        (card, cardIndex) => ({
          media_type: card?.media_type || card?.type || 'image',
          media_url: card?.media_url || card?.media || '',
          caption: card?.caption || card?.text || '',
          buttons: normalizeButtons(
            card?.buttons,
            `block:${blockIndex}:card:${cardIndex}`
          ),
        })
      );
    }

    if (type === 'paid_media') {
      baseBlock.items = Array.isArray(block?.items) ? block.items : [];
      baseBlock.star_count = Number(block?.star_count || 0);
      baseBlock.caption = block?.caption || '';
    }

    return baseBlock;
  })
);

const botName = computed(
  () =>
    campaign.value?.inbox?.bot_name ||
    campaign.value?.inbox?.name ||
    'Telegram Bot'
);

const resolvedBotAvatar = ref('');

const avatarUrl = computed(
  () =>
    resolvedBotAvatar.value ||
    campaign.value?.inbox?.avatar_url ||
    campaign.value?.inbox?.thumbnail ||
    campaign.value?.inbox?.channel?.avatar_url ||
    ''
);

const statusClass = computed(() => {
  const status = campaign.value?.campaign_status;
  if (status === 'completed') return 'status-completed';
  if (status === 'processing') return 'status-processing';
  return 'status-scheduled';
});

const statusLabel = computed(() => {
  const status = campaign.value?.campaign_status;
  if (status === 'completed') return 'Completed';
  if (status === 'processing') return 'Sending';
  return 'Scheduled';
});

watch(
  () => campaign.value?.inbox?.id,
  async inboxId => {
    resolvedBotAvatar.value = '';
    if (!inboxId) return;

    try {
      const response = await telegramAudienceAPI.getBotSettings({
        botInboxId: inboxId,
      });
      const url = String(response.data?.payload?.bot_avatar_url || '').trim();
      if (url) resolvedBotAvatar.value = url;
    } catch {
      // best-effort
    }
  },
  { immediate: true }
);

const backToList = () => {
  router.push({
    name: 'campaigns_telegram_index',
    params: { accountId: route.params.accountId },
  });
};

onMounted(async () => {
  if (!campaign.value) {
    await store.dispatch('campaigns/get');
  }
});
</script>

<template>
  <div class="report-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header class="report-header flex-shrink-0">
      <div class="header-bg">
        <div class="header-glow" />
        <div class="header-pattern" />
      </div>
      <div class="header-inner">
        <div class="header-left">
          <button class="back-btn" @click="backToList">
            <span class="i-ph-arrow-left-bold size-4" />
          </button>
          <div class="header-icon-box">
            <span class="i-ph-chart-bar-bold size-5" />
          </div>
          <div class="header-text">
            <h1 class="header-title">
              {{ campaign?.title || 'Campaign Report' }}
            </h1>
            <p class="header-desc">Telegram campaign analytics</p>
          </div>
        </div>
        <div v-if="campaign" class="header-actions">
          <span class="status-pill" :class="statusClass">
            {{ statusLabel }}
          </span>
        </div>
      </div>
    </header>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center flex-1 py-10">
      <Spinner />
    </div>

    <!-- Not found -->
    <div
      v-else-if="!campaign"
      class="flex flex-col items-center justify-center flex-1 gap-3 py-10"
    >
      <span class="i-ph-warning-circle-bold size-12 text-n-slate-8" />
      <p class="text-sm text-n-slate-10">Campaign not found.</p>
      <button class="back-btn-link" @click="backToList">
        <span class="i-ph-arrow-left-bold size-4" />
        Back to campaigns
      </button>
    </div>

    <!-- Content -->
    <div v-else class="report-content flex-1 overflow-y-auto">
      <div class="report-grid">
        <!-- Left column: metrics + details -->
        <div class="report-main">
          <!-- Metrics -->
          <div class="report-section">
            <h2 class="section-title">
              <span class="i-ph-chart-line-up-bold size-4" />
              Performance
            </h2>
            <div class="metrics-grid">
              <div class="metric-card">
                <div class="metric-icon metric-icon--send">
                  <span class="i-ph-paper-plane-tilt-bold size-5" />
                </div>
                <div class="metric-value">
                  {{ sendCount.toLocaleString() }}
                </div>
                <div class="metric-label">Messages sent</div>
              </div>
              <div class="metric-card">
                <div class="metric-icon metric-icon--click">
                  <span class="i-ph-cursor-click-bold size-5" />
                </div>
                <div class="metric-value">
                  {{ clickCount.toLocaleString() }}
                </div>
                <div class="metric-label">Total clicks</div>
              </div>
              <div class="metric-card">
                <div class="metric-icon metric-icon--ctr">
                  <span class="i-ph-percent-bold size-5" />
                </div>
                <div class="metric-value">{{ ctrValue }}</div>
                <div class="metric-label">Click-through rate</div>
              </div>
            </div>
          </div>

          <!-- Details -->
          <div class="report-section">
            <h2 class="section-title">
              <span class="i-ph-info-bold size-4" />
              Campaign details
            </h2>
            <div class="details-table">
              <div class="details-row">
                <span class="details-key">
                  <span class="i-ph-flag-bold size-3.5" />
                  Status
                </span>
                <span class="details-value">
                  <span class="status-pill" :class="statusClass">
                    {{ statusLabel }}
                  </span>
                </span>
              </div>
              <div class="details-row">
                <span class="details-key">
                  <span class="i-ph-calendar-bold size-3.5" />
                  Sending date
                </span>
                <span class="details-value">
                  {{ formatDateTime(campaign.scheduled_at) }}
                </span>
              </div>
              <div class="details-row">
                <span class="details-key">
                  <span class="i-ph-robot-bold size-3.5" />
                  Bot name
                </span>
                <span class="details-value">{{ botName }}</span>
              </div>
              <div v-if="campaign.inbox?.name" class="details-row">
                <span class="details-key">
                  <span class="i-ph-tray-bold size-3.5" />
                  Inbox
                </span>
                <span class="details-value">{{ campaign.inbox.name }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right column: preview -->
        <aside class="preview-sidebar">
          <div class="preview-sidebar__sticky">
            <div class="preview-sidebar__header">
              <h3 class="preview-title">
                <span class="i-ph-device-mobile-bold size-4" />
                Preview
              </h3>
            </div>
            <div class="preview-sidebar__content">
              <TelegramCampaignPreview
                :bot-name="botName"
                :avatar-url="avatarUrl"
                :image-url="
                  blocksIncludePrimary ? '' : telegramData.image_url || ''
                "
                :message="blocksIncludePrimary ? '' : campaign.message || ''"
                :buttons="previewButtons"
                :blocks="previewBlocks"
                :show-button-click-badges="true"
                :button-click-counts="buttonClickCounts"
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Force light mode */
.report-page,
.dark .report-page {
  background: #ffffff !important;
  --wlab-bg-primary: #ffffff;
  --wlab-bg-input: #f9fafb;
  --wlab-border-default: #e5e7eb;
  --wlab-text-primary: #111827;
  --wlab-text-secondary: #374151;
  --wlab-text-muted: #6b7280;
}

.report-page :deep(.dark\:from-n-background),
.report-page :deep(.from-n-background) {
  --tw-gradient-from: #ffffff var(--tw-gradient-from-position) !important;
}

/* Header */
.report-header {
  position: relative;
  padding: 12px 32px;
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb;
  overflow: hidden;
}

.header-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.header-glow {
  position: absolute;
  top: -50px;
  right: 60px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(99, 102, 241, 0.07) 0%,
    transparent 70%
  );
}

.header-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    rgba(99, 102, 241, 0.04) 1px,
    transparent 1px
  );
  background-size: 20px 20px;
}

.header-inner {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

.back-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.header-icon-box {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(
    135deg,
    rgba(99, 102, 241, 0.15) 0%,
    rgba(139, 92, 246, 0.08) 100%
  );
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #818cf8;
}

.header-text {
  min-width: 0;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #111827;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-desc {
  font-size: 11px;
  color: #6b7280;
  margin: 2px 0 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

/* Status pills */
.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.status-completed {
  background: rgba(107, 114, 128, 0.1);
  color: #374151;
}

.status-processing {
  background: rgba(245, 158, 11, 0.12);
  color: #92400e;
}

.status-scheduled {
  background: rgba(20, 184, 166, 0.12);
  color: #0f766e;
}

/* Report layout */
.report-content {
  background: #ffffff;
}

.report-grid {
  display: grid;
  grid-template-columns: 1fr;
  min-height: 100%;
}

@media (min-width: 1280px) {
  .report-grid {
    grid-template-columns: 1fr 360px;
  }
}

.report-main {
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Sections */
.report-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0;
}

/* Metrics */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 640px) {
  .metrics-grid {
    grid-template-columns: 1fr;
  }
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.15s ease;
}

.metric-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
}

.metric-icon--send {
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
}

.metric-icon--click {
  background: rgba(20, 184, 166, 0.1);
  color: #14b8a6;
}

.metric-icon--ctr {
  background: rgba(0, 102, 255, 0.1);
  color: #0066ff;
}

.metric-value {
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.02em;
  line-height: 1;
}

.metric-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}

/* Details table */
.details-table {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
}

.details-row {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 1rem;
  padding: 14px 20px;
  border-bottom: 1px solid #f3f4f6;
  align-items: center;
}

.details-row:last-child {
  border-bottom: none;
}

.details-key {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
}

.details-value {
  font-size: 13px;
  color: #111827;
}

/* Not found back link */
.back-btn-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: transparent;
  border: none;
  color: #0066ff;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;
}

.back-btn-link:hover {
  color: #ea6b00;
}

/* Preview sidebar */
.preview-sidebar {
  background: #f9fafb;
  border-left: 1px solid #e5e7eb;
}

@media (max-width: 1279px) {
  .preview-sidebar {
    display: none;
  }
}

.preview-sidebar__sticky {
  position: sticky;
  top: 0;
  height: calc(100vh - 65px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.preview-sidebar__header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  background: #ffffff;
}

.preview-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0;
}

.preview-sidebar__content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 16px 16px 24px;
}

.preview-sidebar__content :deep(.telegram-preview-wrap) {
  padding-top: 0.5rem;
  transform: scale(0.88);
  transform-origin: top center;
}
</style>
